// Projects data: the shapes the API returns, cache keys, and the words and colours for statuses and priorities.
export type TaskStatus = "backlog" | "todo" | "in_progress" | "review" | "done";
export type TaskPriority = "none" | "low" | "medium" | "high" | "urgent";
export type ProjectStatus = "planning" | "active" | "on_hold" | "completed" | "cancelled";
export type ProjectRole = "admin" | "lead" | "member" | "client";
type Ref = { id: string; name: string };

export type ProjectSummary = {
  id: string; name: string; key: string; description: string; status: ProjectStatus; visibility: "public" | "private";
  team: { id: string; name: string } | null; startDate: string | null; dueDate: string | null; archived: boolean;
  joined: boolean; myRole: ProjectRole; memberCount: number; clientCount: number;
  tasks: { total: number; done: number; open: number; mine: number; overdue: number };
  createdAt: string; updatedAt: string;
};

export type Project = Omit<ProjectSummary, "team"> & {
  team: { id: string; name: string; channelId: string | null } | null;
  members: { id: string; name: string; email: string; role: "lead" | "member" }[];
  clients: { id: string; name: string; email: string }[];
  pendingClients: { id: string; email: string; expiresAt: string }[];
  clientsEnabled: boolean;
  channels: { id: string; audience: "team" | "client" }[];   // the project's chats this person is in
  can: {
    update: boolean; archive: boolean; members: boolean; createTasks: boolean;
    request: boolean; seeInternal: boolean; inviteClients: boolean; meet: boolean;
  };
};

export type Approval = { status: "pending" | "approved" | "changes_requested"; at: string } | null;

export type Task = {
  id: string; projectId: string; number: number; ref: string; title: string; status: TaskStatus; priority: TaskPriority;
  assignee: Ref | null; dueDate: string | null; position: number; hasDescription: boolean; commentCount: number;
  internal: boolean; clientRequest: boolean; approval: Approval; fromMeeting: boolean; milestoneId: string | null;
  completedAt: string | null; createdAt: string; updatedAt: string;
};

export type MyTask = Task & { project: { id: string; name: string; key: string } };

export type FileRef = { id: string; filename: string; contentType: string; size: number; uploader: Ref | null; createdAt: string };
export type ProjectFile = FileRef & {
  from: { kind: "task" | "comment"; taskId: string; ref: string; title: string } | { kind: "chat"; conversationId: string | null; audience: "team" | "client" };
};
export type TaskComment = { id: string; author: Ref | null; body: string; internal: boolean; editedAt: string | null; createdAt: string; canEdit: boolean; canDelete: boolean; files: FileRef[] };
export type ApprovalRound = {
  id: string; requestedBy: Ref | null; requestedAt: string; decision: "approved" | "changes_requested" | null;
  decidedBy: Ref | null; decidedAt: string | null; note: string;
};
export type Activity = { id: string; kind: string; taskId?: string | null; ref?: string | null; actor: Ref | null; data: Record<string, unknown>; createdAt: string };

export type TaskDetail = {
  task: Task & {
    description: string; createdBy: Ref | null; project: { id: string; name: string; key: string };
    source: { transcriptId: string | null; segmentId: string | null; quote: string; speaker: string; atMs: number } | null;
    milestone: { id: string; name: string } | null; estimateMinutes: number | null;
  };
  comments: TaskComment[];
  activity: Activity[];
  approvals: ApprovalRound[];
  files: FileRef[];
  hasClients: boolean;
  time: TimeLog | null;
  can: { update: boolean; comment: boolean; delete: boolean; seeInternal: boolean; askApproval: boolean; approve: boolean; logTime: boolean };
};

export type TimeLog = { totalMinutes: number; entries: { id: string; person: Ref | null; day: string; minutes: number; note: string; canDelete: boolean }[] };

export type Milestone = { id: string; name: string; description: string; dueDate: string | null; completedAt: string | null; total: number; done: number };

type Brief = { id: string; ref: string; title: string; status: TaskStatus; dueDate: string | null; assignee: Ref | null };
export type Overview = {
  status: Record<TaskStatus, number>; total: number; done: number;
  overdue: Brief[]; overdueCount: number; dueSoon: Brief[];
  milestones: Milestone[];
  team: {
    unassigned: number;
    workload: { person: Ref; open: number; overdue: number }[];
    time: { totalMinutes: number; estimateMinutes: number; byPerson: { person: Ref | null; minutes: number }[] };
  } | null;
  recent: Activity[];
};

// 90 -> "1h 30m"; 45 -> "45m".
export function formatMinutes(total: number): string {
  const h = Math.floor(total / 60), m = total % 60;
  return h ? `${h}h${m ? ` ${m}m` : ""}` : `${m}m`;
}
// What people type for a length of time: "1h 30m", "1h30", "1.5" (hours), "90m", "45 min". Null if it isn't one.
export function parseDuration(text: string): number | null {
  const t = text.trim().toLowerCase().replace(",", ".");
  if (!t) return null;
  const hm = t.match(/^(?:(\d+(?:\.\d+)?)\s*h(?:ours?|rs?)?)?\s*(?:(\d+)\s*m(?:in(?:utes?)?)?)?$/);
  if (hm && (hm[1] || hm[2])) return Math.round(Number(hm[1] ?? 0) * 60 + Number(hm[2] ?? 0)) || null;
  const short = t.match(/^(\d+)h(\d+)$/);
  if (short) return Number(short[1]) * 60 + Number(short[2]);
  const hours = t.match(/^\d+(?:\.\d+)?$/);
  if (hours) return Math.round(Number(t) * 60) || null;
  return null;
}

export const APPROVAL_LABEL: Record<NonNullable<Approval>["status"], string> = {
  pending: "Waiting for approval", approved: "Approved", changes_requested: "Changes requested",
};
export const APPROVAL_STYLE: Record<NonNullable<Approval>["status"], string> = {
  pending: "bg-amber-50 text-amber-800 ring-amber-200 dark:bg-amber-900/30 dark:text-amber-200 dark:ring-amber-800",
  approved: "bg-emerald-50 text-emerald-800 ring-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-200 dark:ring-emerald-800",
  changes_requested: "bg-red-50 text-red-800 ring-red-200 dark:bg-red-900/30 dark:text-red-200 dark:ring-red-800",
};

export type ProjectMeeting = {
  id: string; code: string; url: string; title: string; kind: "instant" | "later" | "scheduled";
  startsAt: string | null; endsAt: string | null; createdAt: string; open: boolean; live: { people: number } | null;
  host: Ref | null;
  recordings: { id: string; title: string; status: string; startedAt: string; durationSeconds: number | null }[];
  transcripts: { id: string; title: string; status: string; startedAt: string; wordCount: number }[];
};

export const projectKeys = {
  list: (slug: string, archived = false) => ["projects", slug, archived ? "archived" : "open"] as const,
  all: (slug: string) => ["projects", slug] as const,
  one: (slug: string, id: string) => ["project", slug, id] as const,
  tasks: (slug: string, id: string) => ["project-tasks", slug, id] as const,
  activity: (slug: string, id: string) => ["project-activity", slug, id] as const,
  task: (slug: string, id: string) => ["task", slug, id] as const,
  mine: (slug: string) => ["my-tasks", slug] as const,
  meetings: (slug: string, id: string) => ["project-meetings", slug, id] as const,
  overview: (slug: string, id: string) => ["project-overview", slug, id] as const,
  files: (slug: string, id: string) => ["project-files", slug, id] as const,
  milestones: (slug: string, id: string) => ["project-milestones", slug, id] as const,
};

export const COLUMNS: { status: TaskStatus; label: string }[] = [
  { status: "backlog", label: "Backlog" },
  { status: "todo", label: "To do" },
  { status: "in_progress", label: "In progress" },
  { status: "review", label: "Review" },
  { status: "done", label: "Done" },
];
export const STATUS_LABEL = Object.fromEntries(COLUMNS.map((c) => [c.status, c.label])) as Record<TaskStatus, string>;

export const STATUS_DOT: Record<TaskStatus, string> = {
  backlog: "bg-slate-400", todo: "bg-sky-500", in_progress: "bg-amber-500", review: "bg-violet-500", done: "bg-emerald-500",
};

export const PRIORITIES: { value: TaskPriority; label: string }[] = [
  { value: "none", label: "No priority" }, { value: "low", label: "Low" }, { value: "medium", label: "Medium" },
  { value: "high", label: "High" }, { value: "urgent", label: "Urgent" },
];
export const PRIORITY_LABEL = Object.fromEntries(PRIORITIES.map((p) => [p.value, p.label])) as Record<TaskPriority, string>;
export const PRIORITY_STYLE: Record<TaskPriority, string> = {
  none: "", low: "text-slate-500", medium: "text-sky-700 dark:text-sky-300", high: "text-amber-700 dark:text-amber-300", urgent: "text-red-700 dark:text-red-300",
};

export const PROJECT_STATUSES: { value: ProjectStatus; label: string }[] = [
  { value: "planning", label: "Planning" }, { value: "active", label: "Active" }, { value: "on_hold", label: "On hold" },
  { value: "completed", label: "Completed" }, { value: "cancelled", label: "Cancelled" },
];
export const PROJECT_STATUS_LABEL = Object.fromEntries(PROJECT_STATUSES.map((s) => [s.value, s.label])) as Record<ProjectStatus, string>;

// Dates are calendar days ("2026-10-31"), shown and compared without a time zone getting in the way.
export const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
export function dayLabel(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  const date = new Date(y!, m! - 1, d!);
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString([], { day: "numeric", month: "short", ...(sameYear ? {} : { year: "numeric" }) });
}
export const isOverdue = (t: Pick<Task, "dueDate" | "status">) => Boolean(t.dueDate && t.status !== "done" && t.dueDate < todayIso());

// One line of a task's or project's history, in words.
export function activityText(a: Activity, withRef = false): string {
  const d = a.data as Record<string, string | null | string[]>;
  const on = withRef && a.ref ? ` ${a.ref}` : "";
  switch (a.kind) {
    case "project.created": return "created the project";
    case "project.updated": return "changed the project's details";
    case "project.status": return `marked the project ${PROJECT_STATUS_LABEL[d.to as ProjectStatus] ?? d.to}`;
    case "project.archived": return "archived the project";
    case "project.restored": return "brought the project back";
    case "member.joined": return "joined the project";
    case "member.left": return "left the project";
    case "member.added": return `added ${(d.people as string[] | undefined)?.join(", ") || "people"}${d.role === "lead" ? " as leads" : ""}`;
    case "member.removed": return `removed ${d.person}`;
    case "member.role": return `made ${d.person} ${d.role === "lead" ? "a lead" : "a member"}`;
    case "task.created": return `created${withRef ? ` ${d.ref} ${d.title}` : " the task"}${d.fromMeeting ? " from a meeting" : ""}`;
    case "meeting.started": return `started a meeting${d.title ? `: ${d.title}` : ""}`;
    case "task.milestone": return d.name ? `put${on || " it"} under ${d.name}` : `took${on || " it"} out of its milestone`;
    case "task.estimate": return d.minutes ? `estimated${on || " it"} at ${formatMinutes(Number(d.minutes))}` : `removed the estimate${on ? ` of${on}` : ""}`;
    case "milestone.created": return `added the milestone ${d.name}`;
    case "milestone.reached": return `marked ${d.name} reached`;
    case "milestone.reopened": return `reopened ${d.name}`;
    case "milestone.deleted": return `removed the milestone ${d.name}`;
    case "meeting.scheduled": return `scheduled ${d.title || "a meeting"}`;
    case "task.renamed": return `renamed${on} to "${d.to}"`;
    case "task.described": return `edited the description${on ? ` of${on}` : ""}`;
    case "task.priority": return `set the priority${on ? ` of${on}` : ""} to ${PRIORITY_LABEL[d.to as TaskPriority] ?? d.to}`;
    case "task.due": return d.to ? `set${on} due ${dayLabel(d.to as string)}` : `removed the due date${on ? ` of${on}` : ""}`;
    case "task.assigned": return d.to ? `gave${on || " it"} to ${d.name}` : `unassigned${on || " it"}`;
    case "task.moved": return `moved${on || " it"} to ${STATUS_LABEL[d.to as TaskStatus] ?? d.to}`;
    case "task.commented": return `commented on${on || " it"}`;
    case "task.deleted": return `deleted ${d.ref} ${d.title}`;
    case "task.requested": return `asked for${withRef ? ` ${d.ref} ${d.title}` : " this"}`;
    case "task.visibility": return d.internal ? `made${on || " it"} team only` : `showed${on || " it"} to the client`;
    case "task.approval_requested": return `asked the client to approve${on || " it"}`;
    case "task.approved": return `approved${on || " it"}`;
    case "task.changes_requested": return `asked for changes${on ? ` to${on}` : ""}${d.note ? `: "${d.note}"` : ""}`;
    case "client.added": return `added ${d.person} as the client`;
    case "client.invited": return `invited ${d.email} as the client`;
    case "client.removed": return `took ${d.person} off the project`;
    default: return a.kind;
  }
}
